import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import VideoCard from "@/components/feature/VideoCard";
import Seo from "@/components/base/Seo";
import { videos } from "@/mocks/videos";

export default function VideoDetail() {
  const { id } = useParams<{ id: string }>();
  const [playing, setPlaying] = useState(false);

  const video = videos.find((v) => v.id === Number(id));

  const related = useMemo(() => {
    if (!video) return [];
    const same = videos.filter((v) => v.category === video.category && v.id !== video.id);
    const rest = videos.filter((v) => v.category !== video.category && v.id !== video.id);
    return [...same, ...rest].slice(0, 6);
  }, [video]);

  if (!video) {
    return (
      <PageShell>
        <div className="container-x flex flex-col items-center justify-center py-24 text-center">
          <i className="ri-video-line text-6xl text-foreground-300" aria-hidden="true"></i>
          <h1 className="mt-4 text-2xl font-bold text-foreground-950">视频不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">你要找的内容可能已被移除，去视频库看看其他精彩内容吧。</p>
          <Link to="/library" className="btn-primary mt-6">
            返回视频库
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Seo
        title={`${video.title} | 瓜趣视频在线观看`}
        description={`${video.summary} 高清流畅秒开播放，${video.category}分类，无需安装即点即看，手机端完美适配。`}
        keywords={`${video.title},${video.category},热门视频在线观看,瓜趣视频`}
        canonical={`https://gagplay6.github.io/video/${video.id}`}
        ogImage={video.cover}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: video.title,
            description: video.summary,
            thumbnailUrl: [video.cover],
            contentUrl: `https://gagplay6.github.io/video/${video.id}`,
            uploadDate: `${video.year}-08-01`,
            inLanguage: "zh-CN",
            genre: video.category,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://gagplay6.github.io/" },
              { "@type": "ListItem", position: 2, name: "视频库", item: "https://gagplay6.github.io/library" },
              { "@type": "ListItem", position: 3, name: video.title, item: `https://gagplay6.github.io/video/${video.id}` },
            ],
          },
        ]}
      />

      <div className="container-x py-8">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to="/library" className="hover:text-primary-600">
            视频库
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="line-clamp-1 text-foreground-700">{video.title}</span>
        </nav>

        <div className="mt-5 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* 播放器 */}
            <div className="relative aspect-video overflow-hidden rounded-lg bg-background-950">
              {playing ? (
                <video
                  className="h-full w-full"
                  controls
                  autoPlay
                  playsInline
                  src="https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4"
                  aria-label={`${video.title} 视频播放`}
                />
              ) : (
                <>
                  <img
                    src={video.cover}
                    alt={video.title}
                    title={`${video.title} - 瓜趣视频`}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30"></div>
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    aria-label="播放视频"
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  >
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-110">
                      <i className="ri-play-fill text-3xl" aria-hidden="true"></i>
                    </span>
                  </button>
                  <span className="absolute bottom-3 right-3 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
                    {video.duration}
                  </span>
                </>
              )}
            </div>

            <h1 className="mt-5 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              {video.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
              <Link to={`/library?category=${encodeURIComponent(video.category)}`} className="chip bg-primary-100 text-primary-700 hover:bg-primary-200">
                {video.category}
              </Link>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {video.views} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line" aria-hidden="true"></i>
                {video.likes} 人喜欢
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true"></i>
                {video.year}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <span key={tag} className="chip bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>

            <section className="card-surface mt-6 p-5" aria-labelledby="desc-title">
              <h2 id="desc-title" className="text-base font-bold text-foreground-950">
                内容简介
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">{video.summary}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本期内容来自瓜趣视频娱乐资讯与热门视频聚合库，高清流畅、极速缓冲，手机端完美适配。点击播放即可立即观看，无需安装任何插件。
              </p>
            </section>
          </div>

          <aside aria-label="相关推荐">
            <h2 className="text-base font-bold text-foreground-950">相关推荐</h2>
            <div className="mt-4 space-y-4">
              {related.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}